import { useMemo, useState } from 'react';
import { ErrorOutlined, MoreHoriz, Refresh } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Link as MuiLink,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import type {
  ConversionJob,
  RawConversionFailure,
  RawUploadGroup,
  ResourceImportSessionSummary,
} from '../store/hooks';

/**
 * The four states the system already records for a source (GDN-3868). These are
 * positions on one path, not new statuses: every label shown on the strip is
 * one `lifecycle()` already produced.
 */
type StageKey = 'uploaded' | 'converted' | 'staged' | 'committed';
const STAGES: { key: StageKey; label: string }[] = [
  { key: 'uploaded', label: 'Uploaded' },
  { key: 'converted', label: 'Converted' },
  { key: 'staged', label: 'Staged' },
  { key: 'committed', label: 'Committed' },
];
const stageIndex = (key: StageKey): number => STAGES.findIndex(s => s.key === key);

/** Visual tone of a status. Always paired with a label, never colour alone. */
type Tone = 'neutral' | 'working' | 'attention' | 'ready' | 'done';

/** Where a status sits on the path, and how it reads. */
interface Stage {
  label: string;
  tone: Tone;
  /** Furthest stage reached; for a working row, the stage in progress. */
  stageKey: StageKey;
  /** Set when the row failed at this stage. */
  failedAt?: StageKey;
}

/** A source retains its upload identity across separately identified outputs. */
interface MigrationUnit {
  id: string;
  label: string;
  fileCount?: number;
  sizeBytes?: number;
  rawPrefix?: string;
  inventoryStatus?: RawUploadGroup['inventory_status'];
  sourceState?: RawUploadGroup['source_state'];
  sessionId?: string;
  status?: string;
  stats?: Record<string, number>;
  createdAt?: string | null;
  conversion?: ConversionJob;
  /** Why the last conversion bootstrap rejected this source, when the server said. */
  conversionFailure?: RawConversionFailure | null;
}

interface MigrationImportsListProps {
  uploads: RawUploadGroup[];
  sessions: ResourceImportSessionSummary[];
  isLoading: boolean;
  hasLoadError?: boolean;
  onRefresh: () => void;
  onOpenBatch: (sessionId: string) => void;
  /** Add more files to a still-pre-conversion batch (raw upload id). */
  onAddFiles: (uploadId: string) => void;
  onDelete?: (uploadId: string) => Promise<void>;
  conversionJobs: ConversionJob[];
  conversionStatusKnown: boolean;
  conversionEnabled: boolean;
  onGo: (uploadId: string, retryOf?: string) => void;
  launchingSourceId: string | null;
  uploadsBusy: boolean;
}

function formatBytes(bytes?: number): string {
  if (!bytes) return '—';
  const units = ['B', 'KiB', 'MiB', 'GiB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Legacy sources can be linked by the trailing converted batch ID. */
function batchIdOf(s3Prefix?: string | null): string {
  if (!s3Prefix) return '';
  const parts = s3Prefix.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? '';
}

/**
 * Operator-facing reason a conversion bootstrap was rejected (GDN-3859).
 *
 * The server sends one authored sentence carrying the limit, the actual value
 * and the allowed value, so that is what an operator reads. The derived
 * fallback covers a blank message: the structured fields are always present,
 * and a row that says only "Conversion failed" is the very thing this fixes.
 */
function conversionFailureReason(failure: RawConversionFailure): string | null {
  const authored = failure.message?.trim();
  if (authored) return authored;
  // Past here the server broke its own contract: `message` is required. Derive
  // a sentence only where one is defensible — a limits refusal, a limit this
  // code understands, and a ceiling to quote. Anything else keeps the chip and
  // says nothing, rather than inventing a reason or throwing mid-render.
  if (failure.code && failure.code !== 'raw_source_exceeds_limits') return null;
  if (failure.limit === 'bytes') {
    const max = failure.max_bytes;
    if (typeof max !== 'number' || max <= 0) return null;
    const observed = failure.total_size_bytes;
    // Only print the observed value when it supports the claim; an inventory
    // number that doesn't exceed the ceiling would make the sentence false.
    const actual =
      typeof observed === 'number' && observed > max ? formatBytes(observed) : 'its size';
    return `Source exceeds the size limit: ${actual} (limit ${formatBytes(max)}).`;
  }
  if (failure.limit !== 'files') return null;
  const max = failure.max_files;
  if (typeof max !== 'number' || max <= 0) return null;
  const observed = failure.file_count;
  // 'en-US' to match the server's own grouping, not the browser's locale.
  const actual =
    typeof observed === 'number' && observed > max
      ? `${observed.toLocaleString('en-US')} files`
      : 'its file count';
  return `Source exceeds the file limit: ${actual} (limit ${max.toLocaleString('en-US')}).`;
}

function isConfirmedEmpty(unit: MigrationUnit): boolean {
  return unit.inventoryStatus === 'ready' && unit.fileCount === 0;
}

/**
 * Map the staging status onto the operator-facing lifecycle stage.
 *
 * Every label here is one the table produced before the Journey reskin; the
 * reskin adds only where that label sits on the path (GDN-3868). A source that
 * has not converted is still at Uploaded, whatever it is doing; a failure marks
 * the stage it failed at and completes the ones before it.
 */
function lifecycle(unit: MigrationUnit): Stage {
  if (unit.sourceState === 'deleting')
    return { label: 'Deleting…', tone: 'working', stageKey: 'uploaded' };
  if (!unit.sessionId) {
    const job = unit.conversion;
    if (!job) {
      if (unit.inventoryStatus === 'refreshing')
        return { label: 'Counting files', tone: 'working', stageKey: 'uploaded' };
      if (unit.inventoryStatus === 'failed')
        return { label: 'File counts unavailable', tone: 'attention', stageKey: 'uploaded' };
      if (isConfirmedEmpty(unit))
        return { label: 'No files found', tone: 'neutral', stageKey: 'uploaded' };
      if (unit.fileCount === undefined || unit.fileCount === 0)
        return { label: 'Upload registered', tone: 'neutral', stageKey: 'uploaded' };
      return { label: 'Uploaded', tone: 'neutral', stageKey: 'uploaded' };
    }
    if (job.status === 'failed' || job.status === 'dead')
      // A published failure got as far as uploading results, so it failed at
      // Staged; an unpublished one never cleared conversion.
      return job.published
        ? { label: 'Needs attention', tone: 'attention', stageKey: 'converted', failedAt: 'staged' }
        : {
            label: 'Conversion failed',
            tone: 'attention',
            stageKey: 'uploaded',
            failedAt: 'converted',
          };
    if (job.status === 'pending')
      return { label: 'Queued', tone: 'working', stageKey: 'converted' };
    if (job.published || job.status === 'completed')
      return { label: 'Staging', tone: 'working', stageKey: 'staged' };
    if (job.phase === 'verifying')
      return { label: 'Verifying', tone: 'working', stageKey: 'converted' };
    if (job.phase === 'publishing_verified_output')
      return { label: 'Uploading results', tone: 'working', stageKey: 'converted' };
    return { label: 'Converting', tone: 'working', stageKey: 'converted' };
  }
  switch (unit.status) {
    case 'ready_for_review':
      return { label: 'Ready to review', tone: 'ready', stageKey: 'staged' };
    case 'importing':
      return { label: 'Committing', tone: 'working', stageKey: 'committed' };
    case 'completed':
      return { label: 'Committed', tone: 'done', stageKey: 'committed' };
    case 'failed':
      return { label: 'Failed', tone: 'attention', stageKey: 'staged', failedAt: 'committed' };
    case 'ingesting':
      return { label: 'Staging', tone: 'working', stageKey: 'staged' };
    default:
      return { label: unit.status ?? 'Staged', tone: 'working', stageKey: 'staged' };
  }
}

/** Theme token for a tone. Colour always travels with a label. */
const TONE_COLOR: Record<Tone, string> = {
  neutral: 'text.secondary',
  working: 'info.main',
  attention: 'error.main',
  ready: 'primary.main',
  done: 'success.main',
};

/**
 * The four-stage strip. The last reached segment carries the row's status
 * label; the others carry the stage name. Each segment states its own state in
 * its accessible name, so the strip is not colour-only.
 */
function StageStrip({ stage, sourceLabel }: { stage: Stage; sourceLabel: string }) {
  const reached = stageIndex(stage.stageKey);
  const failedAt = stage.failedAt ? stageIndex(stage.failedAt) : -1;
  const isWorking = stage.tone === 'working';
  const labelAt = failedAt >= 0 ? failedAt : reached;
  return (
    <Box
      role="group"
      aria-label={`Progress for ${sourceLabel}`}
      // minmax(0, 1fr), not 1fr: a plain 1fr track cannot shrink below its
      // content, so the caption's ellipsis would never engage and one long
      // status would widen the strip instead of truncating.
      sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.75 }}
    >
      {STAGES.map((s, i) => {
        const isFailed = i === failedAt;
        // A source on this list has been uploaded, whatever it is busy with now.
        // Without this, an inventory refresh flips Uploaded between done and in
        // progress on every poll, reading as the upload un-completing.
        const isCurrent = isWorking && i === reached && i > 0;
        const isDone = i === 0 || (isWorking ? i < reached : i <= reached && !isFailed);
        const showsStatus = i === labelAt;
        // An attention row with no failed stage (a stalled inventory, say) still
        // paints red. Say so in the name rather than calling a red bar "done".
        const needsAttention = !isFailed && showsStatus && stage.tone === 'attention';
        let bg = 'action.hover';
        let color = 'text.disabled';
        if (isDone) {
          const takesRowTone = i === reached && failedAt < 0;
          bg = TONE_COLOR[takesRowTone ? stage.tone : 'done'];
          color = 'text.secondary';
        }
        if (isCurrent) {
          bg = TONE_COLOR.working;
          color = 'text.primary';
        }
        if (isFailed) {
          bg = TONE_COLOR.attention;
          color = 'error.main';
        }
        if (showsStatus && !isFailed && !isCurrent) color = 'text.primary';
        const state = isFailed
          ? 'failed'
          : isCurrent
            ? 'in progress'
            : needsAttention
              ? 'needs attention'
              : isDone
                ? 'done'
                : 'not started';
        return (
          <Box key={s.key}>
            {/* The bar carries the role: `img` is presentational-children, so
                naming the wrapper would hide the status label below it. */}
            <Box
              role="img"
              aria-label={`${s.label}: ${state}`}
              sx={{ height: 6, borderRadius: 3, bgcolor: bg }}
            />
            <Typography
              variant="caption"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.5,
                color,
                fontWeight: showsStatus ? 600 : 400,
                // Lets the text child below shrink instead of forcing this row
                // wider than its grid track.
                minWidth: 0,
              }}
              title={showsStatus ? stage.label : undefined}
            >
              {isFailed && <ErrorOutlined sx={{ fontSize: 14, flex: '0 0 auto' }} aria-hidden />}
              {/* The truncation lives here, not on the row above: this caption
                  is a flex container so the failed icon can sit beside the
                  text, and `text-overflow` does nothing on a flex container.
                  The default lifecycle branch renders an unbounded raw server
                  status; the full text stays available on hover. */}
              <Box
                component="span"
                sx={{
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {showsStatus ? stage.label : s.label}
              </Box>
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/** One action a row offers behind the overflow menu. */
interface SecondaryAction {
  key: string;
  label: string;
  ariaLabel?: string;
  disabled?: boolean;
  danger?: boolean;
  onSelect: () => void;
}

/** One primary action, everything else behind an overflow menu. */
function RowActions({
  sourceLabel,
  primary,
  secondary,
}: {
  sourceLabel: string;
  primary?: {
    label: string;
    kind: 'run' | 'retry' | 'view';
    disabled: boolean;
    onClick: () => void;
  };
  secondary: SecondaryAction[];
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
      {primary && (
        <Button
          size="small"
          variant={primary.kind === 'view' ? 'outlined' : 'contained'}
          disabled={primary.disabled}
          onClick={primary.onClick}
          // Several rows can offer the same action; name it per source, as the
          // overflow trigger already does.
          aria-label={`${primary.label} ${sourceLabel}`}
          sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', minWidth: 112 }}
        >
          {primary.label}
        </Button>
      )}
      {secondary.length > 0 && (
        <>
          <Tooltip title="More actions">
            <IconButton
              size="small"
              aria-label={`More actions for ${sourceLabel}`}
              aria-haspopup="menu"
              aria-expanded={Boolean(anchor)}
              onClick={event => setAnchor(event.currentTarget)}
            >
              <MoreHoriz fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {secondary.map(action => (
              <MenuItem
                key={action.key}
                disabled={action.disabled}
                aria-label={action.ariaLabel}
                sx={action.danger ? { color: 'error.main' } : undefined}
                onClick={() => {
                  setAnchor(null);
                  action.onSelect();
                }}
              >
                {action.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
}

/**
 * One unified list of migration units. An "upload" and the "batch" it converts
 * into are two phases of the same source, so each row shows a
 * single source progressing Uploaded → Ready to review → Committed, rather than
 * exposing the raw-uploads and staged-batches tables separately.
 */
export function MigrationImportsList({
  uploads,
  sessions,
  isLoading,
  hasLoadError = false,
  onRefresh,
  onOpenBatch,
  onAddFiles,
  onDelete,
  conversionJobs,
  conversionStatusKnown,
  conversionEnabled,
  onGo,
  launchingSourceId,
  uploadsBusy,
}: MigrationImportsListProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [deleteTarget, setDeleteTarget] = useState<MigrationUnit | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(false);

  const units = useMemo<MigrationUnit[]>(() => {
    const byId = new Map<string, MigrationUnit>();
    for (const u of uploads) {
      byId.set(u.upload_id, {
        id: u.upload_id,
        label: u.label || u.upload_id,
        fileCount: u.file_count ?? undefined,
        sizeBytes: u.total_size_bytes ?? undefined,
        rawPrefix: u.prefix,
        inventoryStatus: u.inventory_status,
        sourceState: u.source_state,
        createdAt: u.created_at,
        conversionFailure: u.conversion_failure,
      });
    }
    for (const job of conversionJobs) {
      const existing = byId.get(job.raw_batch_id);
      if (existing)
        byId.set(job.raw_batch_id, {
          ...existing,
          conversion: job,
          createdAt: existing.createdAt ?? job.created_at,
        });
    }
    // Keep the latest session if an older source has multiple staging attempts.
    const latestFirst = [...sessions].sort(
      (a, b) =>
        (b.created_at ?? '').localeCompare(a.created_at ?? '') ||
        b.import_session_id.localeCompare(a.import_session_id),
    );
    for (const s of latestFirst) {
      const outputId = batchIdOf(s.s3_prefix);
      const conversion = conversionJobs.find(
        job =>
          job.published &&
          job.verified &&
          (job.import_session_id === s.import_session_id || job.output_batch_id === outputId),
      );
      const id = s.raw_batch_id ?? conversion?.raw_batch_id ?? outputId;
      if (!id) continue;
      const existing = byId.get(id) ?? { id, label: id };
      if (existing.sessionId) continue;
      byId.set(id, {
        ...existing,
        sessionId: s.import_session_id,
        status: s.status,
        stats: s.import_stats ?? undefined,
        createdAt: existing.createdAt ?? s.created_at,
      });
    }
    // Newest first when we have a timestamp; otherwise stable by label.
    return [...byId.values()].sort((a, b) => {
      if (a.createdAt && b.createdAt) return b.createdAt.localeCompare(a.createdAt);
      if (a.createdAt) return -1;
      if (b.createdAt) return 1;
      return a.label.localeCompare(b.label);
    });
  }, [uploads, sessions, conversionJobs]);

  const currentDeleteTarget = units.find(unit => unit.id === deleteTarget?.id);
  const deleteEligible = Boolean(
    currentDeleteTarget &&
    conversionStatusKnown &&
    !currentDeleteTarget.conversion &&
    !currentDeleteTarget.sessionId &&
    (!currentDeleteTarget.sourceState || currentDeleteTarget.sourceState === 'available'),
  );

  const confirmDelete = async () => {
    if (
      !deleteTarget ||
      !onDelete ||
      !deleteEligible ||
      uploadsBusy ||
      launchingSourceId ||
      deleting
    )
      return;
    setDeleting(true);
    setDeleteError(false);
    try {
      await onDelete(deleteTarget.id);
      setDeleteTarget(null);
      enqueueSnackbar('Upload deletion requested', { variant: 'success' });
    } catch {
      setDeleteError(true);
    } finally {
      setDeleting(false);
    }
  };

  const copyPrefix = (prefix: string) => {
    void navigator.clipboard.writeText(prefix);
    enqueueSnackbar('Copied S3 prefix', { variant: 'success' });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Imports"
        subheader="Upload files, run the conversion, then review and commit the results."
        action={
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} aria-label="Refresh imports">
              <Refresh />
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent>
        {conversionStatusKnown && !conversionEnabled && uploads.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Conversion is not available here yet. You can continue uploading files.
          </Alert>
        )}
        {!conversionStatusKnown && uploads.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Conversion status is unavailable. Refresh before starting a conversion or adding files.
          </Alert>
        )}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : units.length === 0 ? (
          !hasLoadError && (
            <Typography variant="body2" color="text.secondary">
              No registered imports yet. Upload source files above, or refresh to find existing
              uploads.
            </Typography>
          )
        ) : (
          <Box role="list" aria-label="Imports">
            {units.map((unit, index) => {
              const stage =
                !conversionStatusKnown && !unit.sessionId && !unit.conversion
                  ? ({
                      label: 'Status unavailable',
                      tone: 'neutral',
                      stageKey: 'uploaded',
                    } as const)
                  : lifecycle(unit);
              const staged = unit.stats?.staged;
              const total = unit.stats?.total;
              const openable = Boolean(unit.sessionId);
              const job = unit.conversion;
              const retryable =
                job && !job.published && (job.status === 'failed' || job.status === 'dead');
              const sourceAvailable = !unit.sourceState || unit.sourceState === 'available';
              // Processing is reserved before dispatch, so an unaccepted launch may have
              // no job. Keep recovery available: the server reuses its stable request ID.
              const canStart =
                unit.sourceState !== 'deleting' &&
                unit.sourceState !== 'deleted' &&
                (!job || retryable);
              const canDelete =
                onDelete && sourceAvailable && !job && !unit.sessionId && conversionStatusKnown;
              // The recorded refusal describes the LAST bootstrap, and the server
              // clears it only once a later attempt is accepted — which the uploads
              // poll learns up to 30s late, while the status flips the instant Go is
              // pressed. Show it only where it is still the row's own story (GDN-3859).
              const showFailureReason =
                Boolean(unit.conversionFailure) &&
                !unit.sessionId &&
                launchingSourceId !== unit.id &&
                unit.sourceState !== 'deleting' &&
                unit.sourceState !== 'deleted' &&
                !job?.published &&
                (!job || job.status === 'failed' || job.status === 'dead');
              const failureReason =
                showFailureReason && unit.conversionFailure
                  ? conversionFailureReason(unit.conversionFailure)
                  : null;
              const needsIngestionCheck =
                job?.published &&
                (job.status === 'failed' || job.status === 'dead') &&
                !unit.sessionId;
              // Every guard below is the one this action carried before the reskin.
              const busy = uploadsBusy || Boolean(launchingSourceId) || Boolean(deleteTarget);
              const primary = openable
                ? {
                    label: 'View',
                    kind: 'view' as const,
                    disabled: busy,
                    onClick: () => onOpenBatch(unit.sessionId as string),
                  }
                : unit.rawPrefix && canStart
                  ? {
                      label: retryable ? 'Retry conversion' : 'Run conversion',
                      kind: retryable ? ('retry' as const) : ('run' as const),
                      disabled:
                        isConfirmedEmpty(unit) ||
                        !conversionEnabled ||
                        !conversionStatusKnown ||
                        busy,
                      onClick: () => onGo(unit.id, retryable ? job.job_id : undefined),
                    }
                  : undefined;
              const secondary: SecondaryAction[] = [];
              if (unit.rawPrefix && !openable) {
                if (!job && sourceAvailable)
                  secondary.push({
                    key: 'add-files',
                    label: 'Add files',
                    disabled: !conversionStatusKnown || busy,
                    onSelect: () => onAddFiles(unit.id),
                  });
                if (canDelete)
                  secondary.push({
                    key: 'delete',
                    label: 'Delete upload',
                    // The name the row carried before the reskin, kept so it stays
                    // unique against the confirm dialog's own "Delete upload".
                    ariaLabel: `Delete upload ${unit.label}`,
                    disabled: busy,
                    danger: true,
                    onSelect: () => {
                      setDeleteError(false);
                      setDeleteTarget(unit);
                    },
                  });
                secondary.push({
                  key: 'copy-prefix',
                  label: 'Copy source location',
                  onSelect: () => copyPrefix(unit.rawPrefix as string),
                });
              }
              const meta = [
                unit.fileCount != null ? `${unit.fileCount.toLocaleString('en-US')} files` : null,
                unit.sizeBytes ? formatBytes(unit.sizeBytes) : null,
                total != null ? `${staged != null ? `${staged}/${total}` : total} resources` : null,
                unit.createdAt ? `created ${new Date(unit.createdAt).toLocaleString()}` : null,
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <Box key={unit.id} role="listitem">
                  {index > 0 && <Divider />}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'minmax(220px, 1.2fr) minmax(300px, 2fr) 176px',
                      },
                      gap: { xs: 1.5, md: 3 },
                      alignItems: 'start',
                      py: 2,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
                      >
                        {openable ? (
                          <MuiLink
                            component="button"
                            type="button"
                            disabled={busy}
                            onClick={() => onOpenBatch(unit.sessionId as string)}
                            sx={{ font: 'inherit', textAlign: 'left' }}
                          >
                            {unit.label}
                          </MuiLink>
                        ) : (
                          unit.label
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', display: 'block' }}
                      >
                        {meta}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 0 }} role="status">
                      <StageStrip
                        stage={
                          launchingSourceId === unit.id
                            ? {
                                // Pressing Run or Retry starts a conversion, so
                                // the row is working on Converted — and the
                                // previous attempt's failure is no longer its
                                // story. Keeping it would paint "Starting…"
                                // inside a red segment named failed.
                                label: 'Starting…',
                                tone: 'working' as const,
                                stageKey: 'converted' as const,
                              }
                            : stage
                        }
                        sourceLabel={unit.label}
                      />
                      {failureReason && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {failureReason}
                        </Typography>
                      )}
                      {needsIngestionCheck && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          Results uploaded; ingestion needs checking.
                        </Typography>
                      )}
                    </Box>
                    <RowActions sourceLabel={unit.label} primary={primary} secondary={secondary} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleting) setDeleteTarget(null);
        }}
      >
        <DialogTitle>Delete upload?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete “{deleteTarget?.label}” and its source files? This cannot be undone. Only uploads
            with no conversion or ingestion history can be deleted.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Could not delete this upload. It may have started processing, or deletion is
              unavailable. Refresh the imports list before trying again.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!deleteEligible || deleting || uploadsBusy || Boolean(launchingSourceId)}
            onClick={() => void confirmDelete()}
          >
            {deleting ? 'Deleting…' : 'Delete upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
